import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { WaitingSpinner, GameEndedScreen, getPlayerHeaderLeftButton } from '../shared';
import { useHostPlaying } from '../../hooks/useHostPlaying';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { calculateTimeRemaining } from '../../utils/gameUtils';
import '../shared/TimerBar.css';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';
import { wouldYouRatherPlayerTheme } from '../../themes/cssTheme';

interface WouldYouRatherQuestion {
  questionId?: string;
  id?: string;
  question: string; // "Would you rather... optionA or optionB?"
  options: string[]; // [optionA, optionB]
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  optionA?: string;
  optionB?: string;
  debatePrompt?: string;
  spicyLevel?: 1 | 2 | 3;
  category?: string;
  isWouldYouRather?: boolean;
}

interface WouldYouRatherPlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: any;
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

// Theme colors for Would You Rather — free-room lilac/pink; branding overrides
const THEME = wouldYouRatherPlayerTheme;

// Phase after choosing
type WaitingPhase = 'waiting' | 'result' | 'drinking' | 'loading';

const WouldYouRatherPlayerDisplay: React.FC<WouldYouRatherPlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const hostIsPlaying = useHostPlaying(roomState);
  const [currentQuestion, setCurrentQuestion] = useState<WouldYouRatherQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<{
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
    totalQuestions: number;
    averageScore: number;
  } | null>(null);
  const [myScore, setMyScore] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [choiceSubmitted, setChoiceSubmitted] = useState(false);
  const [showDebateFlip, setShowDebateFlip] = useState(false);
  const [waitingPhase, setWaitingPhase] = useState<WaitingPhase>('waiting');
  const [mySipsThisRound, setMySipsThisRound] = useState(0);
  const isSubmittingRef = useRef<boolean>(false);
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const currentQuestionIdRef = useRef<string | undefined>(undefined);

  const gameEndData = useGameEndScreenData(roomState);

  // Helper to reset question state
  const resetQuestion = () => {
    setSelectedChoice(null);
    setChoiceSubmitted(false);
    setShowDebateFlip(false);
    setWaitingPhase('waiting');
    setMySipsThisRound(0);
    isSubmittingRef.current = false;
  };

  // Handle question change and sync timer from roomState (e.g. after tab focus)
  useEffect(() => {
    if (!roomState.currentQuestion) return;
    const questionId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
    const questionStartTime = (roomState as any).questionStartTime;
    const serverTimeRemaining = calculateTimeRemaining(roomState.currentQuestion, questionStartTime);
    const isNewQuestion = questionId && previousQuestionIdRef.current !== questionId;
    if ((isNewQuestion || !currentQuestion) && questionId) {
      console.log(`🤔 Question changed: ${previousQuestionIdRef.current} -> ${questionId}`);
      setCurrentQuestion(roomState.currentQuestion);
      setTimeRemaining(serverTimeRemaining);
      if (isNewQuestion) {
        resetQuestion();
      }
      previousQuestionIdRef.current = questionId;
      currentQuestionIdRef.current = questionId;
    } else if (questionId && previousQuestionIdRef.current === questionId) {
      setTimeRemaining(serverTimeRemaining);
    }
  }, [roomState.currentQuestion, (roomState as any).questionStartTime, currentQuestion]);

  // When server says game is finished (e.g. reconnection), show game end screen
  const roomGameState = roomState.gameState;
  useEffect(() => {
    if (roomGameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- gameEndData is derived from roomState
  }, [roomGameState, gameEndData]);

  // Sync score from roomState
  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const me = roomState.players.find((p) => p.id === myPlayerId);
      if (me) setMyScore(me.score);
    }
  }, [roomState.players, myPlayerId]);

  // Socket event listeners
  useEffect(() => {
    const handleQuestion = (data: WouldYouRatherQuestion) => {
      if (data.isWouldYouRather) {
        console.log('🤔 Would You Rather question received:', data);
        setCurrentQuestion(data);
        setTimeRemaining(data.timeLimit);
        resetQuestion();
        const questionId = data.questionId || data.id;
        previousQuestionIdRef.current = questionId;
        currentQuestionIdRef.current = questionId;
      }
    };

    const handleQuestionResult = (data: any) => {
      if (data.isWouldYouRather) {
        console.log('🤔 Would You Rather result received:', data);
        const myChoice = Array.isArray(data.choices)
          ? data.choices.find((c: { playerId: string }) => c.playerId === myPlayerId)
          : undefined;
        setMySipsThisRound(myChoice?.mustDrink ? 1 : 0);
        setWaitingPhase('result');
        
        // After showing result, switch to drinking phase
        setTimeout(() => {
          setWaitingPhase('drinking');
        }, 4000);
        
        // After drinking, switch to loading
        setTimeout(() => {
          setWaitingPhase('loading');
        }, 8000);
      }
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: {
        mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
        wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
        totalQuestions: number;
        averageScore: number;
      };
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
    };

    const handleAnswerReceived = (data: {
      questionId: string;
      choiceRecorded?: boolean;
      choice?: string;
    }) => {
      if (data.choiceRecorded) {
        setChoiceSubmitted(true);
        // Flip to debate immediately after choice is recorded
        setTimeout(() => {
          setShowDebateFlip(true);
        }, 1500);
      }
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('answer-received', handleAnswerReceived);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('answer-received', handleAnswerReceived);
    };
  }, [socket, myPlayerId]);

  // Timer countdown
  useEffect(() => {
    if (!currentQuestion) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 100) {
          clearInterval(interval);
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentQuestion]);

  // Handle choice submission
  const handleChoiceClick = (choice: string) => {
    if (choiceSubmitted || !currentQuestion || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setSelectedChoice(choice);

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      console.error('No question ID available');
      isSubmittingRef.current = false;
      return;
    }

    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId,
      answer: choice,
      timestamp: Date.now(),
    });
  };

  // Get spicy level emoji
  const getSpicyEmoji = (level?: number) => {
    switch (level) {
      case 1: return '🌶️';
      case 2: return '🌶️🌶️';
      case 3: return '🔥🔥🔥';
      default: return '🤔';
    }
  };

  // Game ended screen
  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        resetQuestion();
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    const displayLeaderboard = finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? []);
    const displayStatistics = gameStatistics ?? gameEndData?.gameStatistics ?? null;
    return (
      <GameEndedScreen
        gameType={roomState.gameType}
        finalLeaderboard={displayLeaderboard}
        myPlayerId={myPlayerId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        showYouSuffix
        statistics={displayStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  // Waiting state - no question yet
  if (!currentQuestion) {
    return (
      <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
        <Header
          leftButton={getPlayerHeaderLeftButton({
            isHost,
            onBackToGameSelection,
            onGoHome,
            backTooltip: t('gameplay.backToGameSelection'),
            homeTooltip: t('common.tooltips.home'),
          })}
          rightButton={{
            icon: HeaderIcons.refresh,
            onClick: () => window.location.reload(),
            tooltip: t('common.tooltips.refreshPage')
          }}
          roomCode={roomState.roomName}
        />
        <div className="game-player-wrapper">
          <WaitingSpinner
            message={t('gameplay.wouldYouRather.getReady')}
            isLoading={true}
            color={THEME.secondary}
          />
        </div>
      </div>
    );
  }

  // Question display
  const progress = (timeRemaining / currentQuestion.timeLimit) * 100;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);
  const isLowTime = secondsRemaining <= 5;
  const optionA = currentQuestion.optionA || currentQuestion.options[0];
  const optionB = currentQuestion.optionB || currentQuestion.options[1];

  // Render status indicator for after results come in
  const renderResultIndicator = () => {
    if (waitingPhase === 'result' || waitingPhase === 'drinking') {
      return (
        <div style={{ 
          marginTop: '2rem', 
          textAlign: 'center',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            color: THEME.primary,
            marginBottom: '0.5rem',
          }}>
            <span className="drink-bounce" style={{ animation: 'drinkBounce 0.5s ease-in-out infinite' }}>🍺</span>
            <span>{t('gameplay.wouldYouRather.drinkingTime')}</span>
            <span className="drink-bounce" style={{ animation: 'drinkBounce 0.5s ease-in-out infinite 0.25s' }}>🍻</span>
          </div>
          <div style={{ 
            fontSize: '0.9rem', 
            color: THEME.textMuted,
          }}>
            {hostIsPlaying ? (
              mySipsThisRound > 0 ? (
                <>Drink {mySipsThisRound} {mySipsThisRound === 1 ? 'sip' : 'sips'}</>
              ) : (
                <>{t('gameplay.wouldYouRather.noSips')}</>
              )
            ) : (
              <>{t('gameplay.wouldYouRather.checkHostScreen')}</>
            )}
          </div>
        </div>
      );
    }
    
    if (waitingPhase === 'loading') {
      return <WaitingSpinner isLoading={true} message={t('gameplay.wouldYouRather.loadingNext')} color={THEME.secondary} />;
    }
    
    return null;
  };

  return (
    <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
      <Header
        leftButton={getPlayerHeaderLeftButton({
            isHost,
            onBackToGameSelection,
            onGoHome,
            backTooltip: t('gameplay.backToGameSelection'),
            homeTooltip: t('common.tooltips.home'),
          })}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage'),
        }}
        roomCode={roomState.roomName}
      />
      <div className="player-display" style={{ padding: '1rem', paddingTop: '2rem', width: '100%', maxWidth: '100%' }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
        }}>
          {/* Spicy Level (no timer counter on player - avoids sync mismatch with host) */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{
              background: THEME.primaryLight,
              padding: '0.25rem 0.7rem',
              borderRadius: '1rem',
              color: THEME.secondary,
              fontSize: '0.8rem',
              fontWeight: 700,
              border: `1px solid ${THEME.primaryBorder}`,
            }}>
              {getSpicyEmoji(currentQuestion.spicyLevel)} Dilemma #{currentQuestion.questionNumber}
            </span>
          </div>

          {/* Timer Bar */}
          <div style={{
            width: '100%',
            height: '10px',
            background: 'rgba(255,255,255,0.18)',
            borderRadius: '5px',
            overflow: 'hidden',
            opacity: choiceSubmitted ? 0.55 : 1,
          }}>
            <div
              className={isLowTime ? 'timer-bar__slider-fill--warning' : ''}
              style={{
                width: `${progress}%`,
                height: '100%',
                background: isLowTime ? THEME.accent : THEME.primary,
                transition: 'width 0.1s linear',
              }}
            />
          </div>

          {/* Question */}
          {/* Conditional Question Display - only shown when host is playing */}
          {hostIsPlaying && (
            <div
              className="player-dark-panel"
              style={{
              background: 'rgba(255,255,255,0.08)',
              borderRadius: '1rem',
              padding: '1.5rem',
              textAlign: 'center',
              border: `2px solid ${THEME.primaryBorder}`,
            }}>
              <div style={{ 
                fontSize: '1.4rem', 
                lineHeight: '1.5', 
                fontWeight: '600',
                color: 'white',
              }}>
                {currentQuestion.question}
              </div>
            </div>
          )}

          {/* Choice Options or Flip Card */}
          {!choiceSubmitted ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}>
              <button
                onClick={() => handleChoiceClick(optionA)}
                className={`player-dark-option${selectedChoice === optionA ? ' player-dark-option--selected' : ''}`}
                style={{
                  width: '100%',
                  padding: '1.5rem 1rem',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: selectedChoice === optionA ? `4px solid ${THEME.optionA}` : '4px solid rgba(255,255,255,0.2)',
                  background: selectedChoice === optionA ? THEME.optionALight : 'rgba(255,255,255,0.05)',
                  color: selectedChoice === optionA ? THEME.optionA : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '2rem' }}>A</span>
                <span>{optionA}</span>
              </button>
              <div style={{
                textAlign: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: THEME.primary,
                margin: '-0.5rem 0',
              }}>
                OR
              </div>
              <button
                onClick={() => handleChoiceClick(optionB)}
                className={`player-dark-option${selectedChoice === optionB ? ' player-dark-option--selected' : ''}`}
                style={{
                  width: '100%',
                  padding: '1.5rem 1rem',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: selectedChoice === optionB ? `4px solid ${THEME.optionB}` : '4px solid rgba(255,255,255,0.2)',
                  background: selectedChoice === optionB ? THEME.optionBLight : 'rgba(255,255,255,0.05)',
                  color: selectedChoice === optionB ? THEME.optionB : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '2rem' }}>B</span>
                <span>{optionB}</span>
              </button>
            </div>
          ) : (
            // Flip card - Front: Your choice, Back: Debate time
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{
                position: 'relative',
                width: '100%',
                minHeight: '150px',
                perspective: '1000px',
              }}>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  minHeight: '150px',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: showDebateFlip ? 'rotateY(180deg)' : 'rotateY(0deg)',
                }}>
                  {/* Front - Your Choice */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    padding: '1.5rem',
                    background: selectedChoice === optionA ? THEME.optionALight : THEME.optionBLight,
                    border: `3px solid ${selectedChoice === optionA ? THEME.optionABorder : THEME.optionBBorder}`,
                    borderRadius: '1rem',
                    textAlign: 'center',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                  }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>
                      {selectedChoice === optionA ? 'A' : 'B'}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: selectedChoice === optionA ? THEME.optionA : THEME.optionB, marginBottom: '0.5rem' }}>
                      You chose:
                    </div>
                    <div style={{ fontSize: '1rem', opacity: 0.9, lineHeight: 1.5 }}>
                      {selectedChoice}
                    </div>
                  </div>

                  {/* Back - Debate Time */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    padding: '1.5rem',
                    background: `linear-gradient(135deg, ${THEME.primaryBorder} 0%, ${THEME.accentLight} 100%)`,
                    border: `3px solid ${THEME.primary}`,
                    borderRadius: '1rem',
                    textAlign: 'center',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                    boxShadow: `0 0 24px ${THEME.primaryLight}`,
                  }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>💬</div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: THEME.secondary, marginBottom: '0.5rem' }}>
                      {t('gameplay.hotTakes.debateTime')}
                    </div>
                    {currentQuestion.debatePrompt && (
                      <div style={{ fontSize: '1rem', color: THEME.textMuted, fontStyle: 'italic', lineHeight: 1.5 }}>
                        {currentQuestion.debatePrompt}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Waiting for others indicator (before results) */}
              {waitingPhase === 'waiting' && (
                <WaitingSpinner isLoading={false} color={THEME.secondary} />
              )}
              
              {/* Result indicator (after results come in) */}
              {waitingPhase !== 'waiting' && renderResultIndicator()}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes drinkBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
};

export default WouldYouRatherPlayerDisplay;
